/**
 * The second half of §F1's "hydrate store from hash on boot": confirming the ids a shared
 * link asked for, once the catalogues that own them have answered.
 *
 * `lib/permalink.ts` runs before the first render and therefore before `/indicators` and
 * `/layers` exist, so it can only check that an id is *shaped* like one. A link written
 * against an older deploy — or edited by hand — can name an indicator that no longer
 * exists, and the map would then sit empty with no explanation. This component compares
 * what the hash applied against what the server actually serves and cleans up:
 *
 * * **indicators** (base fill, overlays, bivariate Y) — dropped **with a toast**, because
 *   the indicator is what the reader came to see and its silent absence looks like a bug;
 * * **layers** — dropped **silently**: §I3 omits every NONE-access row for
 *   public/field/partner, so an id missing from *this* reader's `/layers` usually means
 *   "not yours to see" rather than "gone", and §I2 does not announce what is withheld.
 * * **the §F5.8 scope** (`sc=`) — *resolved*, not merely checked. `applyToStore` cannot
 *   build one: the store needs the unit's type, admin level, country and name, and none of
 *   those is inferable from an id. So the hash carries the id alone and this component asks
 *   `/hierarchy/units/{id}`. A 404 drops the filter **with a toast**, for the same reason a
 *   missing indicator gets one: the whole point of the link was the filtered view, and an
 *   unfiltered map arriving instead looks like the link worked.
 *
 * It runs at most once per boot (the pending list is cleared as soon as the matching
 * catalogue lands) and fires **no** requests at all when the hash carried no ids — the
 * common case, which must not pay for a `/layers` fetch on, say, the dashboard route.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchConfig,
  fetchIndicators,
  fetchLayers,
  fetchMe,
  fetchUnitDetail,
  queryKeys,
} from '../lib/api';
import {
  explorerVariableIds,
  INDICATORS_THEME,
  themeAllowed,
  themeEntry,
} from '../features/sidebar/themes';
import { openAccess } from '../lib/access';
import { clearPendingPermalinkIds, pendingPermalinkIds } from '../lib/permalink';
import {
  DEFAULT_INDICATOR,
  effectiveLevels,
  scopeFromUnit,
  useAppStore,
  WATERSHED_LEVEL,
} from '../lib/state';
import { toast } from '../lib/toast';

export function PermalinkSync(): null {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const pending = pendingPermalinkIds();
  const wantIndicators = pending.indicators.length > 0;
  const wantLayers = pending.layers.length > 0;
  /**
   * §M3.5: `lv=watershed` is only meaningful for an indicator with native watershed values,
   * and `applyToStore` cannot know — it runs before the first render. So a restored watershed
   * level is a second reason to consult `/indicators`, even for a hash carrying no ids at all
   * (`#lv=watershed`, hand-written). The default level is `1`, so this never fires on a plain
   * load, and on the map route it is a cache read: the sidebar has the same query in flight.
   */
  const wantWatershedCheck = useAppStore((s) => s.reportingLevel) === WATERSHED_LEVEL;

  const indicators = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: wantIndicators || wantWatershedCheck,
  });

  /**
   * `/layers` is role-keyed (§I1/§D5), and `role` only becomes authoritative when §D1's
   * `/auth/me` lands. Waiting for that query — the same cache entry `SessionSync` already
   * populates, so this observer costs no extra request — keeps an analyst's link from
   * having its restricted layers stripped by a `public` catalogue that was merely first.
   */
  const session = useQuery({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    retry: false,
    staleTime: Infinity,
    enabled: wantLayers || wantIndicators,
  });

  /**
   * The Indicators icon's own rule, read from `/meta/config` — see the guest gate below.
   * Same cache entry the rail and the panel already hold, so observing it costs no request.
   */
  const config = useQuery({
    queryKey: queryKeys.config(),
    queryFn: fetchConfig,
    enabled: wantIndicators,
  });

  const layers = useQuery({
    queryKey: queryKeys.layers(role),
    queryFn: fetchLayers,
    enabled: wantLayers && session.isFetched,
  });

  /**
   * **A shared link may not put an indicator back on a guest's explorer** (S3, client
   * review, 2026-09-08).
   *
   * S3 moved the contractual and non-contractual indicator visualisations behind the
   * Indicators icon and made that icon signed-in only. The rail obeys that. The *hash* did
   * not: `#v=map&ind=b21` is hydrated by `lib/permalink.ts` before any catalogue answers,
   * and nothing downstream asked whose explorer it was landing on — so a pre-S3 link
   * shared by a colleague reopened the whole B.2.1 surface for a guest, choropleth, legend
   * block, year bar and all, next to a rail chip that says the feature is not theirs. The
   * same held for `ov=` and `biv=`, which restore indicators through the same list.
   *
   * The gate is the icon's own rule, not a second copy of it: `requires_signin` on the
   * server's `indicators` theme row, through `themeAllowed` — the function the chip is
   * disabled by. Two consequences follow from reading the server rather than restating it.
   * A deploy that opens indicators to guests opens the links too, in one place; and the
   * **baseline variables stay**, because `explorerVariableIds` reads the same table's
   * `indicators` lists and those ids are datasets S3 kept on the explorer (GESI's extension
   * pair, SLM's crop yields). A guest's link to Extension access by gender still works.
   *
   * **That first consequence arrived a day later.** The 2026-09-08 directive turned the read
   * surface open (`features.open_access`, `OPEN_ACCESS` on the server), and because the gate
   * reads `themeAllowed` rather than restating it, every shared indicator link restores in
   * full for a signed-out reader with no change here beyond passing the flag. When
   * permissions return, so does the drop.
   *
   * It waits for `/auth/me` (`session.isFetched`) for the reason the layers query does: the
   * store's `role` starts at `public`, and acting on it early would strip a signed-in
   * reader's own link. Both queries are cache entries `SessionSync` and the rail already
   * hold, so the honesty costs no request.
   *
   * **What it does not prevent** is the one `GET /indicators/{id}/values` the map fires in
   * the moment between hydration and this effect. §I1 serves that row to `public` either
   * way — the client's clause is about the explorer's *surfaces*, and no legend, year bar
   * or paint survives the drop — so the request is a redundancy, not a leak. Stopping it
   * would mean teaching `applyToStore` a role it cannot know before `/auth/me` lands.
   */
  const gated = useRef(false);
  const configData = config.data;
  const served = session.data?.user.role;
  const sessionSettled = session.isFetched;
  useEffect(() => {
    if (gated.current || !wantIndicators || !configData) return;
    if (!sessionSettled) return;

    /**
     * **The role from the answer, not from the render that scheduled this effect.**
     *
     * `SessionSync` writes the store's `role` from the same `/auth/me` answer that flips
     * `isFetched`, in an effect of its own — so the commit where `sessionSettled` first
     * becomes true still closes over `role: 'public'`, and reading it there stripped a
     * signed-in reader's own link. Caught live as an analyst, after the guest half worked.
     *
     * The store is still consulted, because §F3's `?demo=1` switcher is a client-only role
     * a reviewer sets deliberately and the rail obeys it; it can only raise the apparent
     * persona, so the higher of the two is the honest reading. `useAppStore.getState()`
     * rather than the closure for the same reason as above.
     */
    const storeRole = useAppStore.getState().role;
    const effectiveRole = storeRole !== 'public' ? storeRole : (served ?? storeRole);
    if (
      themeAllowed(
        themeEntry(configData, INDICATORS_THEME),
        effectiveRole,
        openAccess(configData),
      )
    ) {
      gated.current = true; // decided: the link stands, and a later demo switch cannot undo it
      return;
    }

    const keep = explorerVariableIds(configData);
    const gone = new Set(pendingPermalinkIds().indicators.filter((id) => !keep.has(id)));
    gated.current = true;
    if (!gone.size) return;

    useAppStore.setState((s) => {
      const indicator = s.indicator && gone.has(s.indicator) ? null : s.indicator;
      return {
        indicator,
        overlays: s.overlays.filter((o) => !gone.has(o.id) && o.id !== indicator),
        // Same rule as the catalogue drop below: bivariate is "the base indicator crossed
        // with a Y", so losing X ends the mode rather than painting an empty 3×3.
        biv:
          !s.biv.on || indicator === null
            ? { on: false, y: null }
            : { on: true, y: s.biv.y && gone.has(s.biv.y) ? null : s.biv.y },
      };
    });
    toast(t('tools.shareIndicatorsGated'));
  }, [wantIndicators, sessionSettled, configData, served, role, t]);

  /**
   * §F5.8 `sc=`. `retry: false` because the only interesting failure is the 404 that means
   * "no such unit" — the §D envelope the shared id earns when it was written against another
   * deploy — and retrying it twice would delay the toast for no gain. It is the same
   * `['unit', id]` cache entry the §F5.7 profile and the search box use, so a scope the
   * reader then clicks on costs nothing.
   */
  const wantScope = pending.scope[0];
  const scopeUnit = useQuery({
    queryKey: queryKeys.unit(wantScope ?? ''),
    queryFn: () => fetchUnitDetail(wantScope as string),
    enabled: Boolean(wantScope),
    retry: false,
  });

  const known = indicators.data;
  useEffect(() => {
    if (!known) return;
    const ids = pendingPermalinkIds().indicators;
    if (ids.length) {
      clearPendingPermalinkIds('indicators');

      // Two ways a shared hash can name something the map must not draw: the id is not in
      // the catalogue at all, or it is there but demo-only (`active: false`). Both are
      // "drop it", so they share one set — but they are worth *saying* differently, because
      // a link that stops working is a bug report and a link to a disabled indicator is not.
      const missing = ids.filter((id) => !known.some((i) => i.id === id));
      const inactive = ids.filter((id) => known.some((i) => i.id === id && !i.active));
      const gone = new Set([...missing, ...inactive]);
      if (gone.size) {
        // **Both cases now drop the indicator** (S3, 2026-09-08). They used to part
        // company: an id the catalogue had never heard of stayed `null`, and a demo-only id
        // — a link to something real that is merely switched off — opened on
        // `DEFAULT_INDICATOR` instead of blank. S3 makes `DEFAULT_INDICATOR` null, because
        // an indicator on a cold explorer is the surface the client asked to move behind
        // the Indicators icon; substituting one here would put it back on a map nobody
        // asked for it on. Overlays and the bivariate Y were always dropped either way:
        // there is no sensible stand-in for "the second thing someone chose".
        const fallback = DEFAULT_INDICATOR;
        useAppStore.setState((s) => {
          const indicator = !s.indicator || !gone.has(s.indicator) ? s.indicator : fallback;
          return {
            indicator,
            overlays: s.overlays.filter((o) => !gone.has(o.id) && o.id !== indicator),
            // §F5.4's X is the base indicator: losing it leaves bivariate mode with nothing
            // to cross, so the mode goes with it rather than painting a blank 3×3.
            biv: !s.biv.on || indicator === null
              ? { on: false, y: null }
              : { on: true, y: s.biv.y && gone.has(s.biv.y) ? null : s.biv.y },
          };
        });
        toast(t(inactive.length ? 'tools.shareInactive' : 'tools.shareUnknown'));
      }
    }

    // §M3.5, after the drop above so it sees the indicator that actually survived: a
    // restored `lv=watershed` only stands if that indicator really has the stratum. The id is
    // passed alongside the answer so that `OperationalHierarchy`, reconciling off the same
    // catalogue landing from a render that predates the correction above, cannot demote the
    // level for an indicator this effect has just replaced (see `reconcileReportingLevel`).
    //
    // P1(b) rides the same call: a shared link naming `lv=1` over a 2025 baseline indicator
    // describes a map that paints nothing, and restoring it verbatim would hand the reader
    // the blank view this whole change exists to stop. The link's level is honoured wherever
    // the indicator has data there, which is every link anyone has shared so far — the snap
    // only moves a level the indicator provably cannot be drawn at.
    const mapped = useAppStore.getState().indicator;
    const meta = mapped ? (known.find((i) => i.id === mapped) ?? null) : null;
    useAppStore
      .getState()
      .reconcileReportingLevel(
        mapped,
        Boolean(meta?.has_watershed_values),
        effectiveLevels(meta),
      );
  }, [known, t]);

  const { data: unit, isError: scopeGone } = scopeUnit;
  useEffect(() => {
    if (!pendingPermalinkIds().scope.length) return;
    if (!unit && !scopeGone) return; // still in flight — the map draws unfiltered meanwhile
    clearPendingPermalinkIds('scope');
    if (unit) {
      // `setScope`, not `setState`: the shared link's `c=` and `lv=` are both already
      // applied, and the action is what reconciles them with the scope (a link naming a
      // Malian watershed and `c=BFA` is a contradiction the scope wins).
      useAppStore.getState().setScope(scopeFromUnit(unit));
    } else {
      // No filter after all, so the link's `lv=` is a whole-programme level — the reading
      // `applyToStore` deliberately withheld while a scope was pending (it must not write
      // `lastAdminLevel` from a level a filter forced). Re-asserting it through the action
      // is what puts the two back in step, on the one path where the filter never lands.
      const { reportingLevel, setReportingLevel } = useAppStore.getState();
      setReportingLevel(reportingLevel);
      toast(t('hierarchy.filterGone'));
    }
  }, [unit, scopeGone, t]);

  const catalogue = layers.data;
  useEffect(() => {
    const ids = pendingPermalinkIds().layers;
    if (!ids.length || !catalogue) return;
    clearPendingPermalinkIds('layers');

    const gone = ids.filter((id) => !catalogue.some((l) => l.id === id));
    if (!gone.length) return;

    useAppStore.setState((s) => {
      const next = new Set(s.layersOn);
      for (const id of gone) next.delete(id);
      return { layersOn: next };
    });
  }, [catalogue]);

  return null;
}

export default PermalinkSync;
